import {
  Button,
  HStack,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
  useColorMode,
} from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { FaTrash } from 'react-icons/fa';
import { useContextSelector } from 'use-context-selector';
import { colors, styled } from '../../Stitches';
import Emoji from '../Emoji';
import EditorContext from './EditorContext';
import { composerEvent } from './useComposerState';

const UntitledWarningText = styled('p', {
  fontSize: '$1',
  fontFamily: '$mono',
});

const UntitledWarning = () => {
  const title = useContextSelector(EditorContext, (ctx) => ctx.document?.title);
  const isUntitled = title === '';

  return isUntitled ? (
    <UntitledWarningText>Your letter is currently untitled.</UntitledWarningText>
  ) : (
    <></>
  );
};

const StyledModalContent = styled('div', {
  borderRadius: '$4',
  borderStyle: 'solid',
  borderWidth: 0.5,

  variants: {
    theme: {
      light: {
        backgroundColor: colors.gray25,
        borderColor: colors.gray600,
      },
      dark: {
        borderColor: colors.dark.whiteAlpha100,
        backgroundColor: colors.dark.whiteAlpha50,
      },
    },
  },
});

const ActionModal = ({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  const { colorMode } = useColorMode();

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent background={'none'} borderRadius={'8px'}>
        <StyledModalContent theme={colorMode}>
          <ModalCloseButton />
          <ModalHeader>{title}</ModalHeader>
          <ModalFooter>{children}</ModalFooter>
        </StyledModalContent>
      </ModalContent>
    </Modal>
  );
};

export const SendLetterModal = () => {
  const composerState = useContextSelector(EditorContext, (ctx) => ctx.composerState);
  const dispatch = useContextSelector(EditorContext, (ctx) => ctx.dispatch);

  const isOpen = composerState.value === 'stagedForSending';
  const onClose = useCallback(() => dispatch(composerEvent({ type: 'CLEAR' })), [dispatch]);
  const onSend = useCallback(() => dispatch(composerEvent({ type: 'SEND_LETTER' })), [dispatch]);

  return (
    <ActionModal onClose={onClose} isOpen={isOpen} title="Are you ready to send this letter?">
      <VStack width="100%" align="end">
        <UntitledWarning />
        <HStack>
          <Button onClick={onClose} variant="outline">
            <Emoji symbol="✍️" spaceRight label="holding-pen" /> Keep writing
          </Button>
          <Button onClick={onSend}>
            <Emoji symbol="📮" label="Emoji of a post box" spaceRight />
            Send Letter
          </Button>
        </HStack>
      </VStack>
    </ActionModal>
  );
};

export const DeleteLetterModal = () => {
  const composerState = useContextSelector(EditorContext, (ctx) => ctx.composerState);
  const dispatch = useContextSelector(EditorContext, (ctx) => ctx.dispatch);

  const isOpen = composerState.value === 'stagedForDeletion';
  const onClose = () => dispatch(composerEvent({ type: 'CLEAR' }));
  const onDelete = () => dispatch(composerEvent({ type: 'DELETE_LETTER' }));

  return (
    <ActionModal onClose={onClose} isOpen={isOpen} title="Delete this draft?">
      <HStack>
        <Button onClick={onClose} variant="outline">
          <Emoji symbol="✍️" spaceRight label="holding-pen" /> Keep writing
        </Button>
        <Button aria-label="Trash" leftIcon={<FaTrash />} onClick={onDelete} colorScheme="red">
          Delete Draft
        </Button>
      </HStack>
    </ActionModal>
  );
};
